/**
 * Browser adapter for the terrain-mask cache: localStorage entries in the
 * previous app's layout ({t, mask} under ert_terrain_mask_<lat3>_<lon3>).
 * Storage errors (corrupt JSON, quota) propagate to the caller.
 */
import type { MaskCache, MaskCacheEntry } from './terrainMask';
import { TERRAIN_CACHE_PREFIX } from './terrainMask';

export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/** A MaskCache over localStorage (or any Storage-like object). */
export function localStorageMaskCache(storage: KeyValueStorage = globalThis.localStorage): MaskCache {
  return {
    get(key: string): MaskCacheEntry | null {
      const raw = storage.getItem(key);
      if (!raw) return null;
      const entry = JSON.parse(raw) as Partial<MaskCacheEntry> | null;
      if (!entry || typeof entry.t !== 'number' || !Array.isArray(entry.mask)) return null;
      return { t: entry.t, mask: entry.mask };
    },
    set(key: string, entry: MaskCacheEntry): void {
      storage.setItem(key, JSON.stringify({ t: entry.t, mask: entry.mask }));
    },
  };
}

/** Remove every cached mask; returns how many keys were removed. */
export function clearTerrainMaskCache(storage: KeyValueStorage = globalThis.localStorage): number {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.indexOf(TERRAIN_CACHE_PREFIX) === 0) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
  return keys.length;
}
